import { redactPayload, scrubString } from './redact';
import type { LogPayload, LoggerProvider, Tags, TagsResolver } from './types';

export class Logger {
  private static instance: Logger;
  private providers: LoggerProvider[] = [];
  private defaultTags: Tags = {};
  private tagResolvers: TagsResolver[] = [];

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  registerProvider(provider: LoggerProvider): void {
    this.providers.push(provider);
  }

  setDefaultTags(tags: Tags): void {
    this.defaultTags = { ...this.defaultTags, ...tags };
  }

  registerTagsResolver(fn: TagsResolver): void {
    this.tagResolvers.push(fn);
  }

  log(payload: LogPayload): void {
    const resolvedTags = this.tagResolvers.reduce<Tags>((acc, fn) => {
      try {
        return { ...acc, ...fn() };
      } catch {
        return acc;
      }
    }, {});

    const enriched: LogPayload = {
      ...payload,
      // priority: defaultTags < resolvers < call-site tags
      tags: { ...this.defaultTags, ...resolvedTags, ...payload.tags },
      timestamp: Date.now(),
      context: {
        route: window.location.pathname,
        ...payload.context,
      },
    };

    // A throw here would escape into apiV2's interceptor ahead of its 401
    // branch, so the fallback must neither rethrow nor pass the raw payload on.
    let safe: LogPayload;
    try {
      safe = redactPayload(enriched);
    } catch {
      safe = {
        type: enriched.type,
        level: enriched.level,
        // Not String(): a hostile toString can throw, inside the one catch.
        message:
          typeof enriched.message === 'string'
            ? scrubString(enriched.message)
            : '',
        tags: { redaction_failed: 'true' },
      };
    }

    this.providers.forEach((p) => {
      try {
        p.log(safe);
      } catch (e) {
        console.error('Logger provider failed', e);
      }
    });
  }

  captureException(
    error: Error,
    context?: LogPayload['context'],
    tags?: Tags
  ): void {
    this.log({
      type: 'JS_ERROR',
      level: 'error',
      message: error.message,
      error,
      context,
      tags,
    });
  }

  captureMessage(
    message: string,
    context?: LogPayload['context'],
    tags?: Tags
  ): void {
    this.log({
      type: 'UNKNOWN',
      level: 'info',
      message,
      context,
      tags,
    });
  }
}
